import mapboxgl from 'mapbox-gl'

import { DEFAULT_BORDER_COLOR, DEFAULT_MAIN_COLOR } from 'src/components/ColorDot'

import { IFeature, IStationInformation, ISelectedStation, IStationStatus, IGBFSDataResponse } from './types'
import { addMapImage } from './utils'

const SOURCE = 'docked-bikes'
const DISPLAY_LOGO_BREAKPOINT = 17

const getDataFromGBFSFeeds = (
  stationInformations: IStationInformation[],
  stationStatuses: IStationStatus[],
  provider: string
): IFeature[] =>
  stationStatuses.map((station: IStationStatus) => {
    const information = stationInformations.find((info: IStationInformation) => info.station_id === station.station_id)
    const { name = '', lon = 0, lat = 0 } = information || {}

    return {
      type: 'Feature',
      properties: {
        numBikesAvailable: station.num_bikes_available,
        numDocksAvailable: station.num_docks_available,
        provider,
        address: name,
        lastReported: station.last_reported,
      },
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
    }
  })

export const addDockedBikes = async (
  currentMap: mapboxgl.Map,
  onMouseEnter: () => void,
  onMouseLeave: () => void,
  setSelectedStation: (station: ISelectedStation) => void,
  gbfsData: IGBFSDataResponse
) => {
  for (const providerId of Object.keys(gbfsData)) {
    const stationInformation = gbfsData[providerId]?.data?.station_information
    const stationStatus = gbfsData[providerId]?.data?.station_status

    if (!stationInformation || !stationStatus) {
      continue
    }

    const features: IFeature[] = getDataFromGBFSFeeds(stationInformation, stationStatus, providerId)

    const sourceId = `${SOURCE}-${providerId}`
    const layerId = `docked-bikes-layer-${providerId}`
    const detailsLayerId = `docked-bikes-details-${providerId}`

    currentMap.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    })

    const provider = gbfsData[providerId].provider

    currentMap.addLayer({
      id: layerId,
      source: sourceId,
      type: 'circle',
      paint: {
        'circle-radius': {
          base: 2,
          stops: [
            [1, 1],
            [14, 6],
            [15, 7],
            [DISPLAY_LOGO_BREAKPOINT, 14],
          ],
        },
        'circle-color': provider.colors && provider.colors.primary ? provider.colors.primary : DEFAULT_MAIN_COLOR,
        'circle-stroke-color':
          provider.colors && provider.colors.secondary ? provider.colors.secondary : DEFAULT_BORDER_COLOR,
        'circle-stroke-width': 0,
      },
    })

    if (provider.logo) {
      await addMapImage(currentMap, provider.logo, providerId)
      currentMap.addLayer({
        id: detailsLayerId,
        source: sourceId,
        type: 'symbol',
        minzoom: DISPLAY_LOGO_BREAKPOINT,
        layout: {
          'icon-image': providerId,
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': false,
        },
      })
    }

    currentMap.on('mouseenter', detailsLayerId, onMouseEnter)
    currentMap.on('mouseleave', detailsLayerId, onMouseLeave)

    const displayBikeStationPopup = (layer: string) => async (e: any) => {
      if (e.features && e.target.getZoom() >= DISPLAY_LOGO_BREAKPOINT) {
        const stations = currentMap.queryRenderedFeatures(e.point, {
          layers: [layer],
        })
        if (stations.length && stations[0]) {
          const station = stations[0]
          if (station.properties) {
            setSelectedStation({
              coordinates: e.lngLat,
              station: station as unknown as IFeature,
              provider,
            })
          }
        }
      }
    }
    currentMap.on('click', layerId, displayBikeStationPopup(layerId))
    currentMap.on('click', detailsLayerId, displayBikeStationPopup(detailsLayerId))
  }
}
